import React, { useState } from 'react';
import { ChatBubbleLeftRightIcon, BugAntIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import FeedbackModal from './FeedbackModal';
import FeedbackErrorBoundary from './FeedbackErrorBoundary';
import useFeedbackRetry from '../../../hooks/useFeedbackRetry';

/**
 * FeedbackButton component - Can be used as floating button or inline navigation button
 * Supports both floating and inline modes with different styling
 */
const FeedbackButton = ({ 
  className = '',
  position = 'bottom-right',
  variant = 'floating', // 'floating', 'sidebar', or 'mobile'
  showLabel = true,
  style = 'primary', // 'primary' or 'secondary'
  icon = 'lightbulb' // 'lightbulb', 'chat', or 'bug'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { pendingCount } = useFeedbackRetry();

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Get the appropriate icon
  const getIconComponent = () => {
    switch (icon) {
      case 'bug':
        return BugAntIcon;
      case 'lightbulb':
        return LightBulbIcon;
      case 'chat':
      default:
        return LightBulbIcon; // Default to lightbulb for feedback
    }
  };
  
  const IconComponent = getIconComponent();
  
  // Get button styling based on style prop
  const getButtonStyles = () => {
    if (style === 'secondary') {
      return 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 focus:ring-gray-500';
    }
    return 'bg-purple-600 hover:bg-purple-700 text-white focus:ring-purple-500';
  };

  // Position classes for floating variant
  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6'
  };

  // Render floating button (original behavior)
  if (variant === 'floating') {
    return (
      <>
        {/* Floating Feedback Button */}
        <button
          onClick={handleOpenModal}
          className={`
            fixed ${positionClasses[position]} z-40
            w-12 h-12 md:w-14 md:h-14
            ${getButtonStyles()}
            shadow-md hover:shadow-lg
            rounded-full transition-all duration-200 ease-out
            hover:scale-105 active:scale-95
            focus:outline-none focus:ring-2 focus:ring-opacity-30
            group
            ${className}
          `}
          aria-label={icon === 'bug' ? 'Report a bug or issue' : 'Share feedback or suggestions'}
          title={icon === 'bug' ? 'Report a bug or issue' : 'Share feedback or suggestions'}
        >
          {/* Icon */}
          <IconComponent className="w-5 h-5 md:w-6 md:h-6 mx-auto transition-transform duration-200 group-hover:scale-110" />
          
          {/* Pending feedback indicator */}
          {pendingCount > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs 
                           rounded-full flex items-center justify-center font-bold">
              {pendingCount > 9 ? '9+' : pendingCount}
            </div>
          )}
          
          {/* Tooltip for desktop */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg
                         opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
                         whitespace-nowrap hidden md:block">
            {icon === 'bug' ? 'Report Bug' : 'Feedback'}
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 
                           border-transparent border-t-gray-900" />
          </div>
        </button>

        {/* Feedback Modal with Error Boundary */}
        <FeedbackErrorBoundary onClose={handleCloseModal}>
          <FeedbackModal 
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        </FeedbackErrorBoundary>
      </>
    );
  }

  // Render sidebar button
  if (variant === 'sidebar') {
    return (
      <>
        <button
          onClick={handleOpenModal}
          className={`w-full mb-3 p-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-indigo-900 dark:hover:text-white rounded-lg transition-all duration-200 hover:shadow-sm dark:hover:shadow-md hover:ring-1 hover:ring-gray-200 dark:hover:ring-white/10 group relative ${className}`}
          title="Share feedback or suggestions"
          aria-label="Open feedback modal"
        >
          <div className="flex items-center">
            <LightBulbIcon className="w-5 h-5 mr-3 transition-all duration-200 group-hover:drop-shadow-sm" />
            {showLabel && (
              <span className="text-sm font-medium transition-all duration-200">
                Feedback
              </span>
            )}
            {/* Pending feedback indicator */}
            {pendingCount > 0 && (
              <div className="ml-auto w-5 h-5 bg-red-500 text-white text-xs 
                             rounded-full flex items-center justify-center font-bold">
                {pendingCount > 9 ? '9+' : pendingCount}
              </div>
            )}
          </div>
        </button>

        {/* Feedback Modal with Error Boundary */}
        <FeedbackErrorBoundary onClose={handleCloseModal}>
          <FeedbackModal 
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        </FeedbackErrorBoundary>
      </>
    );
  }

  // Render mobile bottom nav button
  if (variant === 'mobile') {
    return (
      <>
        <button
          onClick={handleOpenModal}
          className={`
            flex flex-col items-center justify-center
            min-w-[48px] min-h-[48px] px-4 py-2 rounded-xl
            transition-all duration-200 transform
            active:scale-95 navigation-item relative
            text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-50/80 dark:hover:bg-gray-800/50 hover:shadow-sm
            ${className}
          `}
          aria-label="Report a bug or share feedback"
          title="Report a bug or share feedback"
        >
          <IconComponent className="w-6 h-6 mb-1 transition-all duration-200 group-hover:scale-105" />
          {showLabel && (
            <span className="text-xs font-medium leading-none transition-all duration-200">
              Feedback
            </span>
          )}
          {/* Pending feedback indicator */}
          {pendingCount > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs 
                           rounded-full flex items-center justify-center font-bold">
              {pendingCount > 9 ? '9+' : pendingCount}
            </div>
          )}
        </button>

        {/* Feedback Modal with Error Boundary */}
        <FeedbackErrorBoundary onClose={handleCloseModal}>
          <FeedbackModal 
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        </FeedbackErrorBoundary>
      </>
    );
  }

  return null;
};

export default FeedbackButton;